import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { NoBusinessGuard } from "@/components/NoBusinessGuard";
import { ContentDisplay } from "@/components/ContentDisplay";
import { MultiSectionDisplay } from "@/components/MultiSectionDisplay";
import { RepurposeModal } from "@/components/RepurposeModal";
import { GenerationErrorBlock } from "@/components/GenerationErrorBlock";
import { Button } from "@/components/ui/button";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Lightning, Sparkle, CaretDown } from "@phosphor-icons/react";
import { TemplateManager } from "@/components/TemplateManager";
import { UsageWarningBanner } from "@/components/UsageWarningBanner";
import { UpgradeModal } from "@/components/UpgradeModal";
import { AlwaysIncludeIndicator } from "@/components/AlwaysIncludeIndicator";
import { useLocalDraft } from "@/hooks/useLocalDraft";
import { GenerateConfigFields } from "@/components/generate/GenerateConfigFields";
import { PLATFORMS, GOALS, TONES, MULTI_SECTION_TYPES, LOADING_MESSAGES } from "@/lib/generateConstants";
import { QuickStatsWidget } from "@/components/generate/QuickStatsWidget";
import { GenerateNudges } from "@/components/generate/GenerateNudges";
import { QualityCheckDialog } from "@/components/generate/QualityCheckDialog";
import { BriefBanners } from "@/components/generate/BriefBanners";
import { TopicResearchSection } from "@/components/generate/TopicResearchSection";

export default function GeneratePage() {
  const { api, refreshUser, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const { restore, save: saveDraft, clear: clearDraft } = useLocalDraft("nib_draft_generate");

  const [topic, setTopic] = useState("");
  const [researchText, setResearchText] = useState("");
  const [files, setFiles] = useState([]);
  const [uploadedFileIds, setUploadedFileIds] = useState([]);
  const [contentType, setContentType] = useState("");
  const [platform, setPlatform] = useState("");
  const [audience, setAudience] = useState("");
  const [goal, setGoal] = useState("");
  const [cta, setCta] = useState("");
  const [offerAsset, setOfferAsset] = useState("");
  const [toneVariations, setToneVariations] = useState([]);
  const [lengthConstraint, setLengthConstraint] = useState("");
  const [aiModel, setAiModel] = useState("");

  const [availableModels, setAvailableModels] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [generatedContent, setGeneratedContent] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [isMultiSection, setIsMultiSection] = useState(false);
  const [multiSectionContent, setMultiSectionContent] = useState(null);
  const [repurposeOpen, setRepurposeOpen] = useState(false);
  const [hasBrandVoice, setHasBrandVoice] = useState(true);
  const [configOpen, setConfigOpen] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [alwaysIncludeFiles, setAlwaysIncludeFiles] = useState([]);
  const [generationError, setGenerationError] = useState(null);
  const [autoRetryCount, setAutoRetryCount] = useState(0);
  const [quickStats, setQuickStats] = useState(null);
  const [briefId, setBriefId] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [contentTypeOpen, setContentTypeOpen] = useState(false);
  const [profileNudgeDismissed, setProfileNudgeDismissed] = useState(true);
  const [profileIncomplete, setProfileIncomplete] = useState(false);
  const [researching, setResearching] = useState(false);
  const [strategyProfiles, setStrategyProfiles] = useState([]);
  const [strategyProfileId, setStrategyProfileId] = useState("");
  const [defaultStrategyProfileId, setDefaultStrategyProfileId] = useState("");
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showQualityCheck, setShowQualityCheck] = useState(false);
  const [qualityChecklist, setQualityChecklist] = useState({ voice: false, strategy: false, clarity: false });

  const configSummary = useMemo(
    () => [platform, contentType, toneVariations[0]].filter(Boolean).join(" \u00b7 "),
    [platform, contentType, toneVariations]
  );

  const needsDraftQualityCheck = !user?.onboarding?.milestones?.first_draft_quality_checked;

  const markMilestone = async (milestoneId) => {
    try { await api.post(`/auth/onboarding/milestones/${milestoneId}`); await refreshUser(); }
    catch (e) { console.error("markMilestone failed:", e); }
  };

  useEffect(() => {
    if (location.state?.prefill) return;
    const resumeDraft = location.state?.resumeDraft;
    if (resumeDraft) {
      const ctx = resumeDraft.generation_context || {};
      if (ctx.topic) setTopic(ctx.topic); if (ctx.research_text) setResearchText(ctx.research_text);
      if (ctx.content_type) setContentType(ctx.content_type); if (ctx.platform) setPlatform(ctx.platform);
      if (ctx.audience) setAudience(ctx.audience); if (ctx.goal) setGoal(ctx.goal);
      if (ctx.cta) setCta(ctx.cta); if (ctx.offer_asset) setOfferAsset(ctx.offer_asset);
      if (ctx.tone_variations?.length) setToneVariations(ctx.tone_variations);
      if (ctx.length_constraint) setLengthConstraint(ctx.length_constraint); if (ctx.ai_model) setAiModel(ctx.ai_model);
      if (ctx.strategy_profile_id) setStrategyProfileId(ctx.strategy_profile_id);
      if (resumeDraft.sections && Object.keys(resumeDraft.sections).length > 0) {
        setMultiSectionContent({ sections: resumeDraft.sections, section_names: Object.keys(resumeDraft.sections) });
        setIsMultiSection(true);
      }
      return;
    }
    const draft = restore();
    if (draft) {
      if (draft.topic) setTopic(draft.topic); if (draft.researchText) setResearchText(draft.researchText);
      if (draft.contentType) setContentType(draft.contentType); if (draft.platform) setPlatform(draft.platform);
      if (draft.audience) setAudience(draft.audience); if (draft.goal) setGoal(draft.goal);
      if (draft.cta) setCta(draft.cta); if (draft.offerAsset) setOfferAsset(draft.offerAsset);
      if (draft.toneVariations?.length) setToneVariations(draft.toneVariations);
      if (draft.lengthConstraint) setLengthConstraint(draft.lengthConstraint); if (draft.aiModel) setAiModel(draft.aiModel);
      if (draft.strategyProfileId) setStrategyProfileId(draft.strategyProfileId);
    }
  }, [restore]);

  useEffect(() => {
    const timer = setTimeout(() => {
      saveDraft({ topic, researchText, contentType, platform, audience, goal, cta, offerAsset, toneVariations, lengthConstraint, aiModel, strategyProfileId });
    }, 500);
    return () => clearTimeout(timer);
  }, [topic, researchText, contentType, platform, audience, goal, cta, offerAsset, toneVariations, lengthConstraint, aiModel, strategyProfileId, saveDraft]);

  useEffect(() => {
    const fetchModels = async () => { try { const resp = await api.get("/models/available"); setAvailableModels(resp.data.models || []); } catch (e) { console.error("Failed to fetch models:", e); } };
    fetchModels();
  }, [api]);

  useEffect(() => {
    const fetchStats = async () => { try { const resp = await api.get("/analytics/usage?period=30d"); setQuickStats(resp.data?.summary || null); } catch (e) { console.error("Failed to fetch quick stats:", e); } };
    fetchStats();
  }, [api]);

  useEffect(() => {
    const loadDefaults = async () => {
      try {
        const resp = await api.get("/profile"); const p = resp.data;
        const hasVoice = !!(p.your_name || p.your_title || p.positioning_statement || p.who_you_serve || p.voice_profile?.extracted_at);
        setHasBrandVoice(hasVoice);
        setProfileIncomplete(hasVoice && (!p.positioning_statement || !p.differentiators));
        setProfileNudgeDismissed(!!localStorage.getItem("profile_nudge_dismissed"));
        setAudience(prev => prev || p.default_audience || ""); setToneVariations(prev => prev.length > 0 ? prev : (p.default_tone_variations || []));
        setAiModel(prev => prev || p.default_ai_model || ""); setCta(prev => prev || p.default_cta || "");
        setOfferAsset(prev => prev || p.default_offer_asset || ""); setPlatform(prev => prev || p.default_platform || "");
        setContentType(prev => prev || p.default_content_type || ""); setLengthConstraint(prev => prev || p.default_length_constraint || "");
        setDefaultStrategyProfileId(p.default_strategy_profile_id || "");
      } catch (e) { console.error("Failed to load defaults:", e); }
    };
    loadDefaults();
  }, [api]);

  const loadStrategyProfiles = useCallback(async () => {
    try {
      const resp = await api.get("/strategy/profiles");
      setStrategyProfiles(resp.data?.profiles || []);
      const defaultId = resp.data?.default_strategy_profile_id || defaultStrategyProfileId || "";
      if (defaultId) { setDefaultStrategyProfileId(defaultId); setStrategyProfileId(prev => prev || defaultId); }
    } catch (e) { console.error("Failed to load strategy profiles:", e); }
  }, [api, defaultStrategyProfileId]);

  useEffect(() => { loadStrategyProfiles(); }, [loadStrategyProfiles]);

  useEffect(() => {
    const prefill = location.state?.prefill;
    if (location.state?.brief_id && !briefId) setBriefId(location.state.brief_id);
    if (prefill) {
      setTopic(prefill.topic || ""); setResearchText(prefill.research_text || prefill.research_material || "");
      setContentType(prefill.content_type || ""); setPlatform(prefill.platform || ""); setAudience(prefill.audience || "");
      setGoal(prefill.goal || ""); setCta(prefill.cta === "NONE" ? "" : prefill.cta || "");
      setOfferAsset(prefill.offer_asset === "NONE" ? "" : prefill.offer_asset || "");
      setToneVariations(prefill.tone_variations || []);
      setLengthConstraint(prefill.length_constraint === "PLATFORM BEST LENGTH" ? "" : prefill.length_constraint || "");
      setAiModel(prefill.ai_model || prefill.ai_model_used || "");
      if (prefill.brief_id) setBriefId(prefill.brief_id);
      if (prefill.strategy_profile_id) setStrategyProfileId(prefill.strategy_profile_id);
      if (prefill.duplicate) toast.success("Configuration duplicated — ready to regenerate");
    }
  }, [location.state, briefId]);

  useEffect(() => { setIsMultiSection(MULTI_SECTION_TYPES.includes(contentType)); }, [contentType]);

  useEffect(() => {
    if (!generating) return;
    let idx = 0; setLoadingMsg(LOADING_MESSAGES[0]);
    const interval = setInterval(() => { idx = (idx + 1) % LOADING_MESSAGES.length; setLoadingMsg(LOADING_MESSAGES[idx]); }, 3000);
    return () => clearInterval(interval);
  }, [generating]);

  const handleGenerate = async (e) => {
    if (e) e.preventDefault();
    if (!topic || !contentType || !platform || !audience || !goal || toneVariations.length === 0 || !aiModel) {
      if (!audience || !goal || !aiModel) setAdvancedOpen(true);
      toast.error("Please fill in all required fields"); return;
    }
    if (!researchText && files.length === 0) { toast.error("Please add research text or upload files"); return; }

    setGenerating(true); setGeneratedContent(null); setMultiSectionContent(null); setIsSaved(false); setGenerationError(null); setAutoRetryCount(0);

    const fileIds = [];
    for (const file of files) {
      try { const formData = new FormData(); formData.append("file", file); const resp = await api.post("/upload", formData); fileIds.push(resp.data.file_id); }
      catch { toast.error(`Could not read ${file.name}. Please try a different format.`); fileIds.push(null); }
    }
    setUploadedFileIds(fileIds);

    const payload = {
      topic, research_text: researchText, content_type: contentType, platform, audience, goal,
      cta: cta || "NONE", offer_asset: offerAsset || "NONE", tone_variations: toneVariations,
      ai_model: aiModel, file_ids: fileIds, strategy_profile_id: strategyProfileId || null,
    };

    try {
      let resp;
      if (isMultiSection) {
        resp = await api.post("/generate/multi-section", { ...payload, length_constraint: lengthConstraint || "FULL LENGTH — DO NOT TRUNCATE" });
        setMultiSectionContent(resp.data); setGeneratedContent(null);
      } else {
        resp = await api.post("/generate", { ...payload, length_constraint: lengthConstraint || "PLATFORM BEST LENGTH" });
        setGeneratedContent(resp.data); setMultiSectionContent(null);
      }
      clearDraft(); toast.success("Content generated! +10 XP"); refreshUser();
    } catch (err) {
      const data = err.response?.data;
      if (data?.error && data?.error_type) { setGenerationError(data); if (data.error_type === "rate_limit") setShowUpgrade(true); }
      else { if (err.response?.status === 429) setShowUpgrade(true); toast.error(data?.detail || "Content generation failed. Please try again."); }
    } finally { setGenerating(false); }
  };

  const saveDraftToHistory = async () => {
    if (!generatedContent) return;
    const histResp = await api.post("/history", {
      title: generatedContent.suggested_title || `${contentType} - ${topic.substring(0, 60)}`,
      topic, research_material: researchText, content_type: contentType, platform, audience, goal,
      cta: cta || "NONE", offer_asset: offerAsset || "NONE", tone_variations: toneVariations,
      length_constraint: lengthConstraint || "PLATFORM BEST LENGTH", ai_model_used: aiModel,
      generated_core_angle: generatedContent.core_angle, generated_draft: generatedContent.draft,
      generated_why_it_works: generatedContent.why_it_works,
      generated_alternate_hooks: generatedContent.alternate_hooks || [],
      generated_cta_variations: generatedContent.cta_variations,
      brief_id: briefId || null,
      strategy_profile_id: generatedContent?.strategy_profile_used?.strategy_profile_id || strategyProfileId || null,
      strategy_profile_version: generatedContent?.strategy_profile_used?.version || null,
    });
    if (briefId) { try { await api.patch(`/briefs/${briefId}/status`, { status: "used", history_item_id: histResp.data?.history_id || null }); } catch (e) { console.error("Failed to update brief status:", e); } }
    setIsSaved(true); refreshUser();
  };

  const handleSave = async () => {
    if (!generatedContent) return;
    if (needsDraftQualityCheck) { setShowQualityCheck(true); return; }
    await markMilestone("first_draft_quality_checked"); await saveDraftToHistory();
  };

  const handleEdit = () => { setGeneratedContent(null); setMultiSectionContent(null); formRef.current?.scrollIntoView({ behavior: "smooth" }); };

  const handleSaveMultiSection = async (mergedSections) => {
    if (!multiSectionContent) return;
    const sectionsToSave = mergedSections || multiSectionContent.sections;
    const fullDraft = multiSectionContent.section_names.map((name) => `### ${name}\n\n${sectionsToSave[name] || ""}`).join("\n\n---\n\n");
    const msHistResp = await api.post("/history", {
      title: `${contentType} — ${topic.substring(0, 60)}`, topic, research_material: researchText, content_type: contentType, platform, audience, goal,
      cta: cta || "NONE", offer_asset: offerAsset || "NONE", tone_variations: toneVariations,
      length_constraint: lengthConstraint || "FULL LENGTH", ai_model_used: aiModel,
      generated_core_angle: `Multi-section ${contentType}`, generated_draft: fullDraft,
      generated_why_it_works: `Structured ${multiSectionContent.section_names.length}-section output`,
      generated_alternate_hooks: [], generated_cta_variations: null,
      brief_id: briefId || null,
      strategy_profile_id: multiSectionContent?.strategy_profile_used?.strategy_profile_id || strategyProfileId || null,
      strategy_profile_version: multiSectionContent?.strategy_profile_used?.version || null,
    });
    if (briefId) { try { await api.patch(`/briefs/${briefId}/status`, { status: "used", history_item_id: msHistResp.data?.history_id || null }); } catch (e) { console.error("Failed to update brief status:", e); } }
    setIsSaved(true); refreshUser();
  };

  const toggleTone = (tone) => setToneVariations((prev) => prev.includes(tone) ? prev.filter((t) => t !== tone) : [...prev, tone]);

  const handleResearch = async () => {
    setResearching(true);
    try {
      const resp = await api.post("/generate/research", { topic: topic.trim() });
      setResearchText(prev => prev ? prev + "\n\n---\n\n" + resp.data.research : resp.data.research);
      toast.success("Research added to source material");
    } catch (err) { toast.error(err.response?.data?.detail || "Research failed. Try again."); }
    finally { setResearching(false); }
  };

  return (
    <NoBusinessGuard>
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <GenerateNudges hasBrandVoice={hasBrandVoice} profileIncomplete={profileIncomplete}
          profileNudgeDismissed={profileNudgeDismissed}
          onDismissNudge={() => { localStorage.setItem("profile_nudge_dismissed", "true"); setProfileNudgeDismissed(true); }} />

        <UsageWarningBanner />
        <QuickStatsWidget quickStats={quickStats} />

        <form ref={formRef} onSubmit={handleGenerate} className="space-y-6">
          <BriefBanners briefId={briefId} generatedContent={generatedContent} multiSectionContent={multiSectionContent} />

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <TopicResearchSection
              topic={topic} setTopic={setTopic}
              strategyProfileId={strategyProfileId} setStrategyProfileId={setStrategyProfileId}
              strategyProfiles={strategyProfiles} defaultStrategyProfileId={defaultStrategyProfileId}
              showTemplatePicker={showTemplatePicker} setShowTemplatePicker={setShowTemplatePicker}
              onStrategyCloned={(profile) => { loadStrategyProfiles(); if (profile?.strategy_profile_id) setStrategyProfileId(profile.strategy_profile_id); }}
              researchText={researchText} setResearchText={setResearchText}
              researching={researching} onResearch={handleResearch}
              files={files} setFiles={setFiles} uploadedFileIds={uploadedFileIds}
            />
          </motion.div>

          {/* Mobile config toggle */}
          <div className="md:hidden">
            <button type="button" onClick={() => setConfigOpen(!configOpen)} className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-[var(--bg-card)] border border-[var(--border-strong)] text-sm font-medium text-[var(--text-primary)] transition-colors hover:border-[#FF4400]/30" data-testid="config-toggle-btn">
              <span>{configOpen ? "Hide Output Config" : "Configure Output"}</span>
              <div className="flex items-center gap-2">
                {!configOpen && (contentType || platform || toneVariations.length > 0) && (
                  <span className="text-xs text-[var(--text-muted)] truncate max-w-[180px]">{configSummary}</span>
                )}
                <CaretDown size={16} className={`text-[var(--text-muted)] transition-transform duration-200 ${configOpen ? "rotate-180" : ""}`} />
              </div>
            </button>
          </div>

          <div className={`space-y-6 md:!block ${configOpen ? "block" : "hidden md:block"}`}>
            <GenerateConfigFields
              contentType={contentType} setContentType={setContentType} isMultiSection={isMultiSection}
              contentTypeOpen={contentTypeOpen} setContentTypeOpen={setContentTypeOpen}
              platform={platform} setPlatform={setPlatform} toneVariations={toneVariations} toggleTone={toggleTone}
              advancedOpen={advancedOpen} setAdvancedOpen={setAdvancedOpen}
              audience={audience} setAudience={setAudience} goal={goal} setGoal={setGoal}
              cta={cta} setCta={setCta} offerAsset={offerAsset} setOfferAsset={setOfferAsset}
              lengthConstraint={lengthConstraint} setLengthConstraint={setLengthConstraint}
              aiModel={aiModel} setAiModel={setAiModel} availableModels={availableModels}
              PLATFORMS={PLATFORMS} TONES={TONES} GOALS={GOALS}
            />
          </div>

          <TemplateManager templateType="generate"
            currentValues={{ content_type: contentType, platform, audience, goal, cta: cta || "NONE", offer_asset: offerAsset || "NONE", tone_variations: toneVariations, length_constraint: lengthConstraint || "PLATFORM BEST LENGTH", ai_model: aiModel }}
            onLoad={(template) => {
              if (template.content_type) setContentType(template.content_type); if (template.platform) setPlatform(template.platform);
              if (template.audience) setAudience(template.audience); if (template.goal) setGoal(template.goal);
              setCta(template.cta === "NONE" ? "" : template.cta || "");
              setOfferAsset(template.offer_asset === "NONE" ? "" : template.offer_asset || "");
              if (template.tone_variations?.length) setToneVariations(template.tone_variations);
              if (template.length_constraint && template.length_constraint !== "PLATFORM BEST LENGTH") setLengthConstraint(template.length_constraint);
              if (template.ai_model && availableModels.includes(template.ai_model)) setAiModel(template.ai_model);
            }}
          />

          <AlwaysIncludeIndicator onFilesLoaded={setAlwaysIncludeFiles} />

          <AnimatePresence>
            {generationError && (
              <GenerationErrorBlock error={generationError} onRetry={() => handleGenerate()} onDismiss={() => { setGenerationError(null); setAutoRetryCount(0); }} autoRetryCount={autoRetryCount} onAutoRetryStart={() => setAutoRetryCount((c) => c + 1)} />
            )}
          </AnimatePresence>

          <Button type="submit" disabled={generating || availableModels.length === 0} className="w-full bg-[#FF4400] hover:bg-[#FF6633] text-white font-semibold h-14 rounded-md text-base transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_20px_rgba(255,68,0,0.4)] disabled:opacity-50 disabled:hover:translate-y-0" data-testid="generate-submit-button">
            {generating ? (<span className="flex items-center gap-2"><Sparkle size={20} className="animate-spin" />{loadingMsg}</span>) : (<span className="flex items-center gap-2"><Lightning size={20} weight="fill" />Generate Content</span>)}
          </Button>

          {!generating && topic && contentType && platform && audience && goal && toneVariations.length > 0 && aiModel && (
            <button type="button" onClick={() => navigate("/ab-test", { state: { abTest: { topic, research_text: researchText, content_type: contentType, platform, audience, goal, cta: cta || "NONE", offer_asset: offerAsset || "NONE", tone_variations: toneVariations, length_constraint: lengthConstraint || "PLATFORM BEST LENGTH", ai_model: aiModel, file_ids: uploadedFileIds.filter(Boolean), strategy_profile_id: strategyProfileId || null } } })} className="w-full mt-2 flex items-center justify-center gap-2 h-11 rounded-md border border-[var(--border-strong)] text-sm font-medium text-[var(--text-secondary)] hover:text-[#FF4400] hover:border-[#FF4400]/30 transition-colors" data-testid="ab-test-btn">
              <Lightning size={16} weight="duotone" />Generate A/B Variations Instead
            </button>
          )}
        </form>

        <AnimatePresence>
          {generatedContent && (
            <ContentDisplay data={generatedContent} onSave={handleSave} onEdit={handleEdit} isSaved={isSaved} onRepurpose={() => setRepurposeOpen(true)} exportMeta={{ title: topic, content_type: contentType, platform, audience, ai_model: aiModel }} resourcesUsed={alwaysIncludeFiles.map(f => f.display_name)} />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {multiSectionContent && (
            <MultiSectionDisplay data={multiSectionContent} onSave={handleSaveMultiSection} isSaved={isSaved} exportMeta={{ title: topic, content_type: contentType, platform, audience, ai_model: aiModel }} resourcesUsed={alwaysIncludeFiles.map(f => f.display_name)} generationContext={{ content_type: contentType, topic, platform, tone: toneVariations.join(", ") }} aiModel={aiModel} />
          )}
        </AnimatePresence>

        <RepurposeModal isOpen={repurposeOpen} onClose={() => setRepurposeOpen(false)} originalContent={generatedContent?.draft || ""} originalType={contentType} originalPlatform={platform} audience={audience} availableModels={availableModels} defaultTones={toneVariations} defaultModel={aiModel} />

        <QualityCheckDialog open={showQualityCheck} onOpenChange={setShowQualityCheck}
          checklist={qualityChecklist} onChecklistChange={setQualityChecklist}
          onConfirm={async () => { await markMilestone("first_draft_quality_checked"); setShowQualityCheck(false); await saveDraftToHistory(); }} />
      </main>
    </div>
    <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} trigger="generate content" />
    </NoBusinessGuard>
  );
}
